/**
 * @param {{ labelFr?: string, labelEn?: string, label?: string }} entry
 * @param {'fr'|'en'|string} locale
 */
export function getLocaleLabel(entry, locale = "fr") {
  if (!entry) return "";
  if (locale === "en") return entry.labelEn || entry.labelFr || entry.label || "";
  return entry.labelFr || entry.labelEn || entry.label || "";
}

/**
 * @param {import('./appConfigTypes.js').GsdAppConfig} config
 * @returns {string[]}
 */
export function validateAppConfig(config) {
  const warnings = [];
  if (!config) {
    warnings.push("config is required");
    return warnings;
  }
  if (!config.appName) warnings.push("appName is missing");
  if (!config.appShortName) warnings.push("appShortName is missing");
  if (!Array.isArray(config.navGroups) || config.navGroups.length === 0) {
    warnings.push("navGroups must be a non-empty array");
  } else {
    for (const group of config.navGroups) {
      if (!group.id) warnings.push("navGroup.id is missing");
      if (!group.labelFr && !group.labelEn) warnings.push(`navGroup ${group.id || "?"} labels missing`);
      if (!Array.isArray(group.items)) warnings.push(`navGroup ${group.id} items must be an array`);
    }
  }
  if (config.mode && !["demo", "production"].includes(config.mode)) {
    warnings.push(`mode "${config.mode}" is not demo|production`);
  }
  return warnings;
}

/**
 * @param {import('./appConfigTypes.js').GsdAppConfig} config
 * @param {{ role?: string, isAuthenticated?: boolean, locale?: string }} ctx
 */
export function resolveVisibleNavGroups(config, ctx = {}) {
  const { role = "guest", isAuthenticated = false, locale = "fr" } = ctx;
  // "hanshi" est un alias historique propre au domaine Karate (coquille-martial-core).
  // Ce résolveur est générique et sert des plateformes qui n'ont pas ce vocabulaire
  // (ex. PWS : admin/responsable/sales_manager/vendeur) — on vérifie donc toujours
  // le rôle réel en plus de l'alias, jamais l'alias seul, à tous les niveaux de filtre.
  const effectiveRole = role === "admin" ? "hanshi" : role;

  if (!config?.navGroups) return [];

  return config.navGroups
    .filter((group) => {
      if (group.id === "public") return true;
      if (!isAuthenticated) return false;
      const groupRoles = group.roles || [];
      if (groupRoles.length === 0) return true;
      return groupRoles.includes(effectiveRole) || groupRoles.includes(role);
    })
    .map((group) => ({
      ...group,
      label: getLocaleLabel(group, locale),
      items: (group.items || [])
        .filter((item) => {
          if (!item.roles || item.roles.length === 0) return true;
          return item.roles.includes(effectiveRole) || item.roles.includes(role);
        })
        .map((item) => ({
          ...item,
          label: getLocaleLabel(item, locale),
        })),
    }));
}

/**
 * @param {import('./appConfigTypes.js').GsdAppConfig} config
 * @param {string} roleId
 * @param {string} locale
 */
export function findRole(config, roleId, locale = "fr") {
  const role = (config.roles || []).find((r) => r.id === roleId);
  if (!role) return { id: roleId, label: roleId, icon: "" };
  return { ...role, label: getLocaleLabel(role, locale) };
}
